import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Descriptions,
  Tag,
  Space,
  Table,
  App,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Popconfirm,
  Alert,
  Tooltip,
} from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, EditOutlined, FileAddOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { serviceReceiptService } from '@/services/serviceReceipt.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { supplierInvoiceService } from '@/services/supplierInvoice.service';
import { PrintableServiceReceipt } from '@/components/procurement/PrintableServiceReceipt';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { firstNonUuidDisplay } from '@/utils/displayLabels';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

const ServiceReceiptDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();

  const { data: departments = [] } = useDepartmentsForSelection();

  const { data: sr, isLoading } = useQuery({
    queryKey: ['service-receipt', id],
    queryFn: () => serviceReceiptService.getById(id!),
    enabled: !!id,
  });

  const needsPoLookup =
    !!sr?.po_id &&
    (!(sr.po_number || sr.purchaseOrder?.po_number) ||
      !(sr.supplier_name || sr.supplier?.supplier_name) ||
      !(sr.milestone_label || sr.milestone));

  const { data: poFallback } = useQuery({
    queryKey: ['purchase-order', sr?.po_id],
    queryFn: () => purchaseOrderService.getById(sr!.po_id),
    enabled: needsPoLookup,
  });

  const { data: linkedInvoices = [] } = useQuery({
    queryKey: ['supplier-invoices-by-sr', id],
    queryFn: () => supplierInvoiceService.list({ service_receipt_id: id! } as any),
    enabled: !!id,
  });

  useEffect(() => {
    if (!sr || !editOpen) return;
    editForm.setFieldsValue({
      sr_date: sr.sr_date ? dayjs(sr.sr_date) : dayjs(),
      service_provider_reference: sr.service_provider_reference,
      progress_percentage: sr.progress_percentage,
      claimed_value: sr.claimed_value,
      approved_value: sr.approved_value,
      retention_amount: sr.retention_amount,
      department_id: sr.department_id,
      remarks: sr.remarks ?? sr.notes,
    });
  }, [sr, editOpen, editForm]);

  const runAction = async (key: string, fn: () => Promise<unknown>, opts?: { goList?: boolean }) => {
    setActionLoading(key);
    try {
      await fn();
      message.success('Done');
      queryClient.invalidateQueries({ queryKey: ['service-receipt', id] });
      queryClient.invalidateQueries({ queryKey: ['service-receipts'] });
      if (opts?.goList) navigate('/scm/procurement/service-receipts');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const saveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setActionLoading('edit');
      await serviceReceiptService.update(id!, {
        sr_date: values.sr_date.format('YYYY-MM-DD'),
        service_provider_reference: values.service_provider_reference?.trim() || undefined,
        progress_percentage: values.progress_percentage,
        claimed_value: values.claimed_value,
        approved_value: values.approved_value,
        retention_amount: values.retention_amount,
        department_id: values.department_id,
        remarks: values.remarks?.trim() || undefined,
      });
      message.success('Updated');
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ['service-receipt', id] });
      queryClient.invalidateQueries({ queryKey: ['service-receipts'] });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Update failed');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading || !sr) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  const status = sr.status ?? sr.sr_status;
  const poNumber =
    sr.po_number || sr.purchaseOrder?.po_number || poFallback?.po_number || undefined;
  const supplierName =
    sr.supplier_name ||
    sr.supplier?.supplier_name ||
    poFallback?.supplier_name ||
    poFallback?.supplier?.supplier_name ||
    undefined;
  const milestoneFromPo = poFallback?.milestones?.find((m) => m.id === sr.po_milestone_id);
  const milestoneLabel =
    sr.milestone_label ||
    (sr.milestone
      ? `#${sr.milestone.milestone_number} ${sr.milestone.milestone_name || ''}`.trim()
      : null) ||
    (milestoneFromPo
      ? `#${milestoneFromPo.milestone_number} ${milestoneFromPo.milestone_name || ''}`.trim()
      : null);
  const deptFromList = sr.department_id
    ? departments.find((d) => d.id === sr.department_id)
    : undefined;
  const deptLabel =
    sr.department_label ||
    (sr.department
      ? `${sr.department.department_code} - ${sr.department.department_name}`
      : null) ||
    (deptFromList ? `${deptFromList.department_code} - ${deptFromList.department_name}` : null) ||
    '—';

  const approvedValue = Number(sr.approved_value ?? 0);
  const billedValue = Number(sr.billed_value ?? 0);
  const pendingBillable = Math.max(0, approvedValue - billedValue);
  const isPosted = status === 'Posted';
  const canCreateBill = isPosted && pendingBillable > 0.01;

  const workflowHint =
    status === 'Draft'
      ? 'Next: Submit → Approve → Post (accrues expense to GL). After posting you can create a Purchase Bill and pay via Payment Voucher.'
      : status === 'Submitted'
        ? 'Next: Approve, then Post to accrue GL. Purchase bill is available after Post.'
        : status === 'Approved'
          ? 'Next: Post to accrue expense vs Accrued AP. Then create a Purchase Bill for payment.'
          : status === 'Posted'
            ? pendingBillable > 0.01
              ? `Posted and ready to invoice. Pending billable: ${pendingBillable.toFixed(2)}. Create a Purchase Bill, then pay from the bill or Payment Vouchers.`
              : 'Fully billed. Open linked purchase bills below to pay or review.'
            : null;

  const goCreateBill = () =>
    navigate(`/scm/procurement/supplier-invoices/new?service_receipt_id=${encodeURIComponent(id!)}`);

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/service-receipts')}>
        Back
      </Button>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          {sr.sr_number}
        </Title>
        <Space wrap>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
            Print
          </Button>
          <ReportDocumentActions
            reportKey="service-receipt"
            params={{ id: sr.id }}
            documentId={sr.id}
            sendPermissions={['procurement.grn.edit', 'procurement.po.edit']}
            emailLabel="Email supplier"
          />
          {status === 'Draft' && (
            <>
              <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => serviceReceiptService.submit(id!))}>
                Submit
              </Button>
              <Popconfirm
                title="Cancel this draft service receipt?"
                onConfirm={() => runAction('cancel', () => serviceReceiptService.cancel(id!), { goList: true })}
              >
                <Button danger loading={actionLoading === 'cancel'}>
                  Cancel
                </Button>
              </Popconfirm>
            </>
          )}
          {status === 'Submitted' && (
            <>
              <Button loading={actionLoading === 'approve'} onClick={() => runAction('approve', () => serviceReceiptService.approve(id!))}>
                Approve
              </Button>
              <Popconfirm
                title="Cancel this submitted service receipt?"
                onConfirm={() => runAction('cancel', () => serviceReceiptService.cancel(id!), { goList: true })}
              >
                <Button danger loading={actionLoading === 'cancel'}>
                  Cancel
                </Button>
              </Popconfirm>
            </>
          )}
          {status === 'Approved' && (
            <>
              <Button type="primary" loading={actionLoading === 'post'} onClick={() => runAction('post', () => serviceReceiptService.post(id!))}>
                Post
              </Button>
              <Popconfirm
                title="Cancel this approved service receipt?"
                onConfirm={() => runAction('cancel', () => serviceReceiptService.cancel(id!), { goList: true })}
              >
                <Button danger loading={actionLoading === 'cancel'}>
                  Cancel
                </Button>
              </Popconfirm>
            </>
          )}
          {isPosted && (
            <>
              <Tooltip title={canCreateBill ? undefined : 'No pending billable value left on this receipt'}>
                <Button type="primary" icon={<FileAddOutlined />} disabled={!canCreateBill} onClick={goCreateBill}>
                  Create Purchase Bill
                </Button>
              </Tooltip>
              <Popconfirm
                title="Unpost this service receipt? Milestone received value and accrual JV will be reversed."
                onConfirm={() => runAction('unpost', () => serviceReceiptService.unpost(id!))}
              >
                <Button loading={actionLoading === 'unpost'} disabled={billedValue > 0.01}>
                  Unpost
                </Button>
              </Popconfirm>
            </>
          )}
          {!isPosted && status !== 'Cancelled' && (
            <Tooltip title="Post the service receipt first to accrue GL, then create the purchase bill">
              <Button icon={<FileAddOutlined />} disabled>
                Create Purchase Bill
              </Button>
            </Tooltip>
          )}
        </Space>
      </Space>

      {workflowHint && (
        <Alert
          type={isPosted ? 'success' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message="Service receipt → Purchase bill → Payment"
          description={workflowHint}
        />
      )}

      <Card title="Summary" style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Status">
            <Tag color={statusColors[status] ?? 'default'}>{status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date">{dayjs(sr.sr_date).format('DD MMM YYYY')}</Descriptions.Item>
          <Descriptions.Item label="Service provider reference">
            {sr.service_provider_reference || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="PO">
            <DocumentRefLink
              to={sr.po_id ? `/scm/procurement/purchase-orders/${sr.po_id}` : undefined}
              number={poNumber}
              id={sr.po_id}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Milestone">
            {firstNonUuidDisplay(milestoneLabel)}
          </Descriptions.Item>
          <Descriptions.Item label="Supplier">{supplierName ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Department">{deptLabel}</Descriptions.Item>
          <Descriptions.Item label="Progress %">{sr.progress_percentage ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Claimed">{Number(sr.claimed_value ?? 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Approved">{approvedValue.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Retention">{Number(sr.retention_amount ?? 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Billed">{billedValue.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Pending billable">
            <strong>{pendingBillable.toFixed(2)}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="GL posted">
            {sr.gl_posted ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
          {sr.notes || sr.remarks ? (
            <Descriptions.Item label="Notes" span={3}>
              {sr.notes ?? sr.remarks}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      <Card
        title="Linked purchase bills"
        style={{ marginBottom: 16 }}
        extra={
          canCreateBill ? (
            <Button type="link" size="small" icon={<FileAddOutlined />} onClick={goCreateBill}>
              Create bill
            </Button>
          ) : null
        }
      >
        {linkedInvoices.length === 0 ? (
          <Typography.Text type="secondary">
            {isPosted
              ? 'No purchase bills yet. Use Create Purchase Bill to invoice this service receipt, then pay via Payment Voucher.'
              : 'Purchase bills appear here after this receipt is posted and billed.'}
          </Typography.Text>
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={linkedInvoices}
            columns={[
              {
                title: 'Bill #',
                dataIndex: 'invoice_number',
                render: (v: string, r: any) => (
                  <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-invoices/${r.id}`)}>
                    {v}
                  </Button>
                ),
              },
              { title: 'Status', dataIndex: 'invoice_status' },
              {
                title: 'Total',
                dataIndex: 'grand_total',
                align: 'right' as const,
                render: (v: number) => Number(v ?? 0).toFixed(2),
              },
              {
                title: 'Balance',
                dataIndex: 'balance_due',
                align: 'right' as const,
                render: (v: number) => Number(v ?? 0).toFixed(2),
              },
              {
                title: 'Actions',
                render: (_: unknown, r: any) =>
                  r.gl_posted && Number(r.balance_due ?? 0) > 0 ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/fin/payment-vouchers/new?supplierInvoiceId=${encodeURIComponent(r.id)}`
                        )
                      }
                    >
                      Pay
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      {sr.lines && sr.lines.length > 0 && (
        <Card title="Lines">
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={sr.lines}
            columns={[
              { title: '#', dataIndex: 'line_number', width: 60 },
              {
                title: 'Description',
                render: (_, r) => (
                  <>
                    <div>{r.description || r.item_description || '—'}</div>
                    <LineSpecificationsText value={r.specifications} />
                  </>
                ),
              },
              { title: 'Qty', dataIndex: 'work_quantity' },
              { title: 'UOM', dataIndex: 'uom' },
              {
                title: 'Value',
                dataIndex: 'line_value',
                align: 'right' as const,
                render: (v: number) => Number(v ?? 0).toFixed(2),
              },
              {
                title: 'Billed',
                dataIndex: 'billed_value',
                align: 'right' as const,
                render: (v: number) => Number(v ?? 0).toFixed(2),
              },
              { title: 'Remarks', dataIndex: 'remarks' },
            ]}
          />
        </Card>
      )}
      <div className="print-only" style={{ display: 'none' }}>
        <PrintableServiceReceipt sr={sr} />
      </div>

      <Modal
        title="Edit Service Receipt"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        confirmLoading={actionLoading === 'edit'}
        destroyOnClose
        width={560}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="sr_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="service_provider_reference" label="Service provider reference">
            <Input />
          </Form.Item>
          <Form.Item name="progress_percentage" label="Progress %">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="claimed_value" label="Claimed value" rules={[{ required: true }]}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="approved_value" label="Approved value">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="retention_amount" label="Retention">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceReceiptDetailPage;
